// Glyphs drawn as SVG from a card's rings. Every measure comes from
// src/config/glyph.ts, which also says what each part means. The drawing is
// locked by tests/unit/glyph.test.ts.
//
// The sigil: one ring per direction, inner ring first. The index: the same
// value as a row of small marks along a baseline, outer ring on the left,
// on the top edge of a card where it stays visible when cards overlap.

import { GLYPH, MARK_NAMES, type MarkName } from "../config/glyph.ts"
import { html, joinHtml, type Html } from "../boundary/html.ts"
import type { Ring } from "../domain/rings.ts"

const C = GLYPH.centre

export type Realm = "rise" | "still" | "sink" | "whole"

// The realm a card belongs to, by its outer ring. Its colour follows.
export const realmOf = (rings: readonly Ring[]): Realm => {
  const t = rings.at(-1)
  if (t === "folded") return "whole"
  return t === 1 ? "rise" : t === -1 ? "sink" : "still"
}

export const radii = (width: number): readonly number[] => {
  if (width === 1) return [GLYPH.singleRadius]
  const step = GLYPH.ringSpan / (width - 1)
  return Array.from({ length: width }, (_, i) => GLYPH.innerRadius + i * step)
}

const f = (n: number): number => Math.round(n * 100) / 100

const pt = (r: number, deg: number): readonly [number, number] => {
  const a = (deg * Math.PI) / 180
  return [f(C + r * Math.cos(a)), f(C + r * Math.sin(a))]
}

const markOf = (ring: number): MarkName => MARK_NAMES[ring % MARK_NAMES.length] ?? "bud"

// Half a ring: the top half when up, the bottom half when down.
const halfArc = (r: number, up: boolean): string => {
  const [x1, y1] = pt(r, 180)
  const [x2, y2] = pt(r, 0)
  return `M${x1} ${y1} A${f(r)} ${f(r)} 0 0 ${up ? 1 : 0} ${x2} ${y2}`
}

const out = (r: number, deg: number, len: number): string => {
  const [x1, y1] = pt(r, deg)
  const [x2, y2] = pt(r + len, deg)
  return `M${x1} ${y1} L${x2} ${y2}`
}

// The mark a ring grows at its top (up) or bottom (down). Angles are SVG
// angles: -90 is straight up, 90 straight down.
const ringMark = (name: MarkName, r: number, up: boolean): Html => {
  const top = up ? -90 : 90
  const m = GLYPH.marks
  switch (name) {
    case "bud": {
      const [bx, by] = pt(r + m.bud.at, top)
      return html`<path d="${out(r, top, m.bud.stem)}"/><circle cx="${bx}" cy="${by}" r="${m.bud.radius}" class="g-fill"/>`
    }
    case "fork": {
      const [sx, sy] = pt(r + m.fork.stem, top)
      const dir = up ? -1 : 1
      const reach = f(sy + dir * m.fork.reach)
      return html`<path d="${out(r, top, m.fork.stem)} M${sx} ${sy} L${f(sx - m.fork.spread)} ${reach} M${sx} ${sy} L${f(sx + m.fork.spread)} ${reach}"/>`
    }
    case "crown":
      return html`<path d="${out(r, top - m.crown.angle, m.crown.side)} ${out(r, top, m.crown.middle)} ${out(r, top + m.crown.angle, m.crown.side)}"/>`
    case "cap": {
      const capR = r + m.cap.lift
      const [ax, ay] = pt(capR, top - m.cap.angle)
      const [bx, by] = pt(capR, top + m.cap.angle)
      return html`<path d="${out(r, top, m.cap.stem)} M${ax} ${ay} A${f(capR)} ${f(capR)} 0 0 1 ${bx} ${by}"/>`
    }
  }
}

const ringSvg = (i: number, r: number, ring: Ring): Html => {
  if (ring === "folded") return html`<g class="g-ring g-folded" data-ring="${i}"><circle cx="${C}" cy="${C}" r="${f(r)}" class="g-band"/></g>`
  const orbit = html`<circle cx="${C}" cy="${C}" r="${f(r)}" class="g-orbit"/>`
  if (ring === 0) {
    const [ex, ey] = pt(r, 0)
    const [wx, wy] = pt(r, 180)
    const n = GLYPH.levelNode
    return html`<g class="g-ring" data-ring="${i}">${orbit}<circle cx="${ex}" cy="${ey}" r="${n}" class="g-fill"/><circle cx="${wx}" cy="${wy}" r="${n}" class="g-fill"/></g>`
  }
  const up = ring === 1
  return html`<g class="g-ring" data-ring="${i}">${orbit}<path class="g-bold" d="${halfArc(r, up)}"/>${ringMark(markOf(i), r, up)}</g>`
}

const monadBand = (r: number, i: number): Html => {
  const node = (a: number): Html => {
    const [x, y] = pt(r, a - 90 + i * GLYPH.monad.nodeTurn)
    return html`<circle cx="${x}" cy="${y}" r="${GLYPH.monad.node}" class="g-fill"/>`
  }
  return html`<g class="g-ring g-folded" data-ring="${i}"><circle cx="${C}" cy="${C}" r="${f(r)}" class="g-halo"/><circle cx="${C}" cy="${C}" r="${f(r)}" class="g-band"/>${[0, 120, 240].map(node)}</g>`
}

const ray = (from: number, n: number) => (k: number): string => {
  const deg = (k * 360) / n - 90
  const len = GLYPH.monad.rays[k % 3] ?? 0
  const [x1, y1] = pt(from, deg)
  const [x2, y2] = pt(from + len, deg)
  return `M${x1} ${y1} L${x2} ${y2}`
}

// The Monad: every ring a thin band of light, each with three nodes turned a
// little further than the ring inside it, a small core, and one fine ray for
// every card of the deck it was folded from, in three lengths.
const monadSvg = (rs: readonly number[], cls: string): Html => {
  const n = 3 ** rs.length
  const rays = Array.from({ length: n }, (_, k) => k).map(ray((rs.at(-1) ?? 0) + GLYPH.monad.rayGap, n))
  return html`<svg class="sigil monad ${cls}" viewBox="0 0 ${2 * C} ${2 * C}" aria-hidden="true">${rs.map(monadBand)}<circle cx="${C}" cy="${C}" r="${GLYPH.monad.core}" class="g-fill g-core"/><path class="g-rays" d="${rays.join(" ")}"/></svg>`
}

const isFoldedRing = (r: Ring): boolean => r === "folded"

export const sigil = (rings: readonly Ring[], cls = ""): Html => {
  const rs = radii(rings.length)
  if (rings.length > 0 && rings.every(isFoldedRing)) return monadSvg(rs, cls)
  const drawn = rings.map((ring, i) => ringSvg(i, rs[i] ?? 0, ring))
  return html`<svg class="sigil ${cls}" viewBox="0 0 ${2 * C} ${2 * C}" aria-hidden="true">${drawn}</svg>`
}

// The same marks as a ring's, drawn small on a stalk at x along a baseline.
const indexMark = (name: MarkName, x: number, up: boolean): Html => {
  const g = GLYPH.index
  const d = up ? -1 : 1
  const y0 = g.baseline
  const tip = y0 + d * g.stalk
  const stalk = `M${x} ${y0} L${x} ${tip}`
  switch (name) {
    case "bud":
      return html`<path d="${stalk}"/><circle cx="${x}" cy="${tip + d * g.bud}" r="${g.bud}" class="g-fill"/>`
    case "fork": {
      const fork = y0 + d * g.fork.at
      return html`<path d="M${x} ${y0} L${x} ${fork} M${x} ${fork} L${x - g.fork.spread} ${fork + d * g.fork.reach} M${x} ${fork} L${x + g.fork.spread} ${fork + d * g.fork.reach}"/>`
    }
    case "crown":
      return html`<path d="${stalk} M${x} ${y0 + d * g.crown.from} L${x - g.crown.spread} ${tip - d * g.crown.short} M${x} ${y0 + d * g.crown.from} L${x + g.crown.spread} ${tip - d * g.crown.short}"/>`
    case "cap":
      return html`<path d="M${x} ${y0} L${x} ${y0 + d * g.cap.stem} M${x - g.cap.spread} ${tip + d * g.cap.lift} Q${x} ${tip + d * g.cap.bow} ${x + g.cap.spread} ${tip + d * g.cap.lift}"/>`
  }
}

const indexPlace = (rings: readonly Ring[], p: number): Html => {
  const g = GLYPH.index
  const ringIndex = rings.length - 1 - p
  const t = rings[ringIndex] ?? 0
  const x = 2 + g.gap / 2 + p * g.gap
  const m =
    t === "folded"
      ? html`<path class="g-fill" d="M${x} ${g.folded.top} L${x + g.folded.half} ${g.baseline} L${x} ${g.height - g.folded.top} L${x - g.folded.half} ${g.baseline} Z"/>`
      : t === 0
        ? html`<circle cx="${x}" cy="${g.baseline}" r="${g.node}" class="g-fill"/>`
        : indexMark(markOf(ringIndex), x, t === 1)
  return html`<g data-ring="${ringIndex}">${m}</g>`
}

// The index row: outer ring first, left to right. A folded ring is a bar
// through the baseline, reaching both ways.
export const indexRow = (rings: readonly Ring[]): Html => {
  const g = GLYPH.index
  const width = rings.length * g.gap + 4
  const marks = rings.map((_, p) => indexPlace(rings, p))
  return html`<svg class="index" viewBox="0 0 ${width} ${g.height}" aria-hidden="true"><path class="g-base" d="M1 ${g.baseline} L${width - 1} ${g.baseline}"/>${joinHtml(marks)}</svg>`
}
